import { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Container,
  Drawer,
  FileInput,
  Loader,
  Group,
  Menu,
  Modal,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconArrowsRight,
  IconBolt,
  IconClipboardText,
  IconDotsVertical,
  IconEdit,
  IconId,
  IconSearch,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { ApiError, api } from "../api/client";
import type {
  AntennaImportResult,
  Batch,
  Device,
  ImportMapping,
  ImportPreview,
  ImportResult,
  Shipment,
} from "../api/types";
import {
  ImportColumnMapper,
  loadSavedMapping,
  mappingValid,
  saveMapping,
} from "../components/batchImport/ImportColumnMapper";
import { antennaErrorMessage } from "../lib/antennaErrors";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { PrintPassportLabelsModal } from "../components/shipments/PrintPassportLabelsModal";

const PAGE_SIZE = 50;

export function BatchDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const batchId = Number(id);
  const user = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canEdit = user.role === "admin" || user.role === "operator";
  const canDelete = user.role === "admin";

  const [batch, setBatch] = useState<Batch | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [importOpen, setImportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passportOpen, setPassportOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [moveBatchOpen, setMoveBatchOpen] = useState(false);
  const [moveShipmentOpen, setMoveShipmentOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [markDefectOpen, setMarkDefectOpen] = useState(false);
  const [defectReason, setDefectReason] = useState("");
  const [allBatches, setAllBatches] = useState<Batch[]>([]);
  const [allShipments, setAllShipments] = useState<Shipment[]>([]);

  async function load() {
    setLoading(true);
    try {
      const [b, devs] = await Promise.all([
        api.getBatch(batchId),
        api.listBatchDevices(batchId),
      ]);
      setBatch(b);
      setDevices(devs);
    } catch {
      notifications.show({ color: "red", title: t("common.error"), message: t("batchDetail.loadFailed") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (Number.isFinite(batchId)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  useEffect(() => {
    void api.listBatches().then(setAllBatches).catch(() => undefined);
    void api.listShipments().then(setAllShipments).catch(() => undefined);
  }, []);

  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllOnPage(visible: Device[]) {
    setSelected((prev) => {
      const allSelected = visible.every((d) => prev.has(d.id));
      const next = new Set(prev);
      if (allSelected) {
        for (const d of visible) next.delete(d.id);
      } else {
        for (const d of visible) next.add(d.id);
      }
      return next;
    });
  }

  async function applyBulk(updates: Record<string, unknown>, successMsg: string) {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const r = await api.bulkUpdateDevices(ids, updates);
      notifications.show({
        color: r.updated > 0 ? "green" : "yellow",
        message: `${successMsg} · ${r.updated}/${r.total}`,
      });
      setSelected(new Set());
      await load();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("common.operationFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  async function deleteSelected() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(t("batchDetail.confirmDeleteDevices", { count: ids.length }))) return;
    try {
      const r = await api.bulkDeleteDevices(ids);
      notifications.show({ color: "green", message: t("batchDetail.devicesDeleted", { count: r.deleted }) });
      setSelected(new Set());
      await load();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("batchDetail.deleteFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  async function handleDelete(force = false) {
    const msg = force
      ? t("batchDetail.confirmDeleteBatchForce", { count: devices.length })
      : t("batchDetail.confirmDeleteBatch", { code: batch?.code });
    if (!window.confirm(msg)) return;
    try {
      await api.deleteBatch(batchId, force);
      notifications.show({ color: "green", message: t("batchDetail.batchDeleted") });
      navigate("/batches");
    } catch (err) {
      const detail = err instanceof ApiError ? (err.detail as { error?: string; device_count?: number }) : null;
      if (detail?.error === "batch_not_empty" && !force) {
        if (
          window.confirm(
            t("batchDetail.confirmForceFollowup", { count: detail.device_count }),
          )
        ) {
          void handleDelete(true);
        }
        return;
      }
      notifications.show({
        color: "red",
        title: t("batchDetail.deleteFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  if (!Number.isFinite(batchId)) return <Text c="red">{t("batchDetail.invalidId")}</Text>;

  const defectCount = devices.filter((d) => d.status === "defect").length;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? devices.filter((d) =>
        [
          d.dev_eui,
          d.join_eui,
          d.app_key,
          d.model,
          d.serial_number,
          d.firmware_version,
          d.box_number,
          d.qr_payload,
          d.status,
          d.shipment_id != null ? `#${d.shipment_id}` : null,
        ]
          .filter((v): v is string => !!v)
          .some((v) => v.toLowerCase().includes(q)),
      )
    : devices;
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const pageDevices = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container size="xl">
      <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
        <Anchor component={Link} to="/batches" size="sm">
          <Group gap={4}>
            <IconArrowLeft size={14} />
            {t("batchDetail.back")}
          </Group>
        </Anchor>
        <Group gap="xs" wrap="wrap">
          <Button
            component={Link}
            to={`/batches/${batchId}/flash`}
            variant="light"
            leftSection={<IconBolt size={16} />}
          >
            {t("batchDetail.flashFirmware")}
          </Button>
          {canEdit && (
            <Button leftSection={<IconUpload size={16} />} onClick={() => setImportOpen(true)}>
              {batch?.product_type_id === 2 ? t("antennas.importAntennas") : t("batchDetail.importDevices")}
            </Button>
          )}
          {canEdit && (
            <Button variant="default" leftSection={<IconId size={16} />} onClick={() => setPassportOpen(true)}>
              {t("passportLabels.title")}
            </Button>
          )}
          {canEdit && (
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <Button variant="default">
                  <IconDotsVertical size={16} />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => setEditOpen(true)}>
                  {t("batchDetail.editBatch")}
                </Menu.Item>
                {canDelete && (
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => handleDelete(false)}
                  >
                    {t("batchDetail.deleteBatch")}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>

      <Title order={2} mb="xs">
        {batch?.code ?? "..."}
      </Title>
      <Group mb="lg" wrap="wrap" gap="xs">
        <Text c="dimmed" size="sm">{t("batchDetail.supplier", { name: batch?.supplier_name ?? t("common.none") })}</Text>
        <Text c="dimmed" size="sm">{t("batchDetail.received", { date: batch?.received_date ?? t("common.none") })}</Text>
        <Badge variant="light">{t("batchDetail.devicesCount", { count: devices.length })}</Badge>
        {devices.length > 0 && (
          <Badge color={defectCount > 0 ? "red" : "gray"} variant="light">
            {t("batchDetail.defectStat", {
              n: defectCount,
              total: devices.length,
              pct: ((defectCount / devices.length) * 100).toFixed(1),
            })}
          </Badge>
        )}
      </Group>

      <Group mb="sm" wrap="wrap" gap="sm">
        <TextInput
          placeholder={t("batchDetail.searchPlaceholder")}
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          rightSection={
            search ? (
              <ActionIcon variant="subtle" color="gray" onClick={() => setSearch("")}>
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
          style={{ flex: "1 1 240px", maxWidth: 420 }}
        />
        {search && (
          <Text size="sm" c="dimmed">
            {t("batchDetail.devicesCounter", {
              filtered: filtered.length,
              total: devices.length,
            })}
          </Text>
        )}
      </Group>

      {canEdit && selected.size > 0 && (
        <Paper withBorder p="xs" mb="xs">
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm" fw={500}>
              {t("batchDetail.selected", { count: selected.size })}
            </Text>
            <Group gap="xs" wrap="wrap">
              <Button
                size="xs"
                leftSection={<IconArrowsRight size={12} />}
                onClick={() => setMoveBatchOpen(true)}
              >
                {t("batchDetail.moveToBatch")}
              </Button>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconArrowsRight size={12} />}
                onClick={() => setMoveShipmentOpen(true)}
              >
                {t("batchDetail.moveToShipment")}
              </Button>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconEdit size={12} />}
                onClick={() => setBulkEditOpen(true)}
              >
                {t("batchDetail.editN", { count: selected.size })}
              </Button>
              {canEdit && (
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  onClick={() => setMarkDefectOpen(true)}
                >
                  {t("batchDetail.markDefectBulk", { count: selected.size })}
                </Button>
              )}
              {user.role === "admin" && (
                <Button
                  size="xs"
                  color="red"
                  variant="subtle"
                  leftSection={<IconTrash size={12} />}
                  onClick={deleteSelected}
                >
                  {t("common.delete")}
                </Button>
              )}
              <Button size="xs" variant="subtle" onClick={() => setSelected(new Set())}>
                {t("common.cancel")}
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      <ResponsiveTable
        mobile={{
          rows: pageDevices,
          rowKey: (d) => d.id,
          title: (d) => <Text span ff="monospace" size="sm" fw={600}>{d.dev_eui.toLowerCase()}</Text>,
          subtitle: (d) => d.model ?? undefined,
          badge: (d) => <Badge size="sm" variant="light">{d.status}</Badge>,
          fields: [
            { label: "APP_EUI", render: (d) => <Text ff="monospace" size="sm">{d.join_eui?.toLowerCase() ?? t("common.none")}</Text> },
            { label: "APP_KEY", render: (d) => <Text ff="monospace" size="sm" style={{ wordBreak: "break-all" }}>{d.app_key?.toLowerCase() ?? t("common.none")}</Text> },
            {
              label: t("nav.shipments"),
              render: (d) => d.shipment_id ? (
                <Anchor component={Link} to={`/shipments/${d.shipment_id}`} size="sm">#{d.shipment_id}</Anchor>
              ) : (
                <Text c="dimmed" size="sm">{t("common.none")}</Text>
              ),
            },
          ],
          actions: canEdit ? (d) => (
            <Button size="compact-md" variant="subtle" onClick={() => setEditingDevice(d)}>
              <IconEdit size={18} />
            </Button>
          ) : undefined,
          selection: canEdit ? {
            isSelected: (d) => selected.has(d.id),
            toggle: (d) => toggleSelected(d.id),
            allSelected: pageDevices.length > 0 && pageDevices.every((d) => selected.has(d.id)),
            someSelected: pageDevices.some((d) => selected.has(d.id)) && !pageDevices.every((d) => selected.has(d.id)),
            toggleAll: () => toggleSelectAllOnPage(pageDevices),
          } : undefined,
          loading: loading && devices.length === 0,
          empty: devices.length === 0 ? t("batchDetail.noDevices") : t("batchDetail.noResults", { q: search }),
        }}
        stickyColumns={canEdit ? 2 : 1}
      >
      <Table.ScrollContainer minWidth={780}>
      <Table striped withTableBorder highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {canEdit && (
              <Table.Th w={36}>
                <Checkbox
                  checked={
                    pageDevices.length > 0 && pageDevices.every((d) => selected.has(d.id))
                  }
                  indeterminate={
                    pageDevices.some((d) => selected.has(d.id)) &&
                    !pageDevices.every((d) => selected.has(d.id))
                  }
                  onChange={() => toggleSelectAllOnPage(pageDevices)}
                  aria-label={t("batchDetail.selectAllOnPage")}
                />
              </Table.Th>
            )}
            <Table.Th>DEV_EUI</Table.Th>
            <Table.Th>APP_EUI</Table.Th>
            <Table.Th>APP_KEY</Table.Th>
            <Table.Th>{t("batchDetail.modelLabel")}</Table.Th>
            <Table.Th style={{ whiteSpace: "nowrap" }}>{t("batchDetail.statusLabel")}</Table.Th>
            <Table.Th>{t("nav.shipments")}</Table.Th>
            {canEdit && <Table.Th w={50} />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={canEdit ? 8 : 6}>
                <Text c="dimmed">{t("common.loading")}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && devices.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={canEdit ? 8 : 6}>
                <Text c="dimmed">{t("batchDetail.noDevices")}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && devices.length > 0 && filtered.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={canEdit ? 8 : 6}>
                <Text c="dimmed">{t("batchDetail.noResults", { q: search })}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {pageDevices.map((d) => (
            <Table.Tr key={d.id}>
              {canEdit && (
                <Table.Td>
                  <Checkbox
                    checked={selected.has(d.id)}
                    onChange={() => toggleSelected(d.id)}
                    aria-label={t("batchDetail.selectOne", { eui: d.dev_eui })}
                  />
                </Table.Td>
              )}
              <Table.Td><Text ff="monospace">{d.dev_eui.toLowerCase()}</Text></Table.Td>
              <Table.Td><Text ff="monospace" size="sm">{d.join_eui?.toLowerCase() ?? t("common.none")}</Text></Table.Td>
              <Table.Td><Text ff="monospace" size="sm" truncate maw={200}>{d.app_key?.toLowerCase() ?? t("common.none")}</Text></Table.Td>
              <Table.Td><Text size="sm">{d.model ?? t("common.none")}</Text></Table.Td>
              <Table.Td style={{ whiteSpace: "nowrap" }}>
                <Badge size="sm" variant="light">{d.status}</Badge>
              </Table.Td>
              <Table.Td>
                {d.shipment_id ? (
                  <Anchor component={Link} to={`/shipments/${d.shipment_id}`} size="sm">
                    #{d.shipment_id}
                  </Anchor>
                ) : (
                  <Text c="dimmed" size="sm">{t("common.none")}</Text>
                )}
              </Table.Td>
              {canEdit && (
                <Table.Td>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => setEditingDevice(d)}
                  >
                    <IconEdit size={14} />
                  </Button>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      </ResponsiveTable>

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}

      <ImportAntennasModal
        opened={importOpen && batch?.product_type_id === 2}
        onClose={() => setImportOpen(false)}
        batchId={batchId}
        onImported={() => load()}
      />

      <ImportDevicesModal
        opened={importOpen && batch?.product_type_id !== 2}
        onClose={() => setImportOpen(false)}
        batchId={batchId}
        onImported={() => {
          setImportOpen(false);
          load();
        }}
      />

      <PrintPassportLabelsModal
        opened={passportOpen}
        onClose={() => setPassportOpen(false)}
      />

      {batch && (
        <EditBatchDrawer
          opened={editOpen}
          onClose={() => setEditOpen(false)}
          batch={batch}
          onSaved={(updated) => {
            setBatch(updated);
            setEditOpen(false);
          }}
        />
      )}

      <MoveTargetModal
        opened={moveBatchOpen}
        onClose={() => setMoveBatchOpen(false)}
        title={t("batchDetail.moveBatchTitle")}
        label={t("batchDetail.moveBatchLabel")}
        options={allBatches
          .filter((b) => b.id !== batchId)
          .map((b) => ({
            value: String(b.id),
            label: `${b.code}${b.supplier_name ? ` · ${b.supplier_name}` : ""}`,
          }))}
        onConfirm={async (value) => {
          await applyBulk({ batch_id: Number(value) }, t("batchDetail.movedToBatch"));
          setMoveBatchOpen(false);
        }}
      />

      <MoveTargetModal
        opened={moveShipmentOpen}
        onClose={() => setMoveShipmentOpen(false)}
        title={t("batchDetail.moveShipmentTitle")}
        label={t("batchDetail.moveShipmentLabel")}
        options={[
          { value: "__none__", label: t("batchDetail.removeFromShipment") },
          ...allShipments.map((s) => ({
            value: String(s.id),
            label: `${s.code} · ${s.status}`,
          })),
        ]}
        onConfirm={async (value) => {
          const updates: Record<string, unknown> =
            value === "__none__"
              ? { shipment_id: null, box_number: null }
              : { shipment_id: Number(value) };
          await applyBulk(updates, t("batchDetail.movedToShipment"));
          setMoveShipmentOpen(false);
        }}
      />

      <BulkEditModal
        opened={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        count={selected.size}
        onConfirm={async (updates) => {
          await applyBulk(updates, t("batchDetail.edited"));
          setBulkEditOpen(false);
        }}
      />

      <SingleDeviceEditDrawer
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        onSaved={(d) => {
          setDevices((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x)));
          setEditingDevice(null);
        }}
      />

      <Modal
        opened={markDefectOpen}
        onClose={() => setMarkDefectOpen(false)}
        title={t("batchDetail.markDefectTitle")}
        centered
      >
        <Stack>
          <Text size="sm">{t("batchDetail.markDefectMsg", { count: selected.size })}</Text>
          <TextInput
            label={t("batchDetail.markDefectReason")}
            value={defectReason}
            onChange={(e) => setDefectReason(e.currentTarget.value)}
            placeholder={t("batchDetail.markDefectPlaceholder")}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setMarkDefectOpen(false)}>{t("common.cancel")}</Button>
            <Button color="red" onClick={async () => {
              try {
                await api.bulkUpdateDevices(Array.from(selected), { status: "defect", notes_append: defectReason || "marked defect" });
                setMarkDefectOpen(false);
                setDefectReason("");
                setSelected(new Set());
                await load();
                notifications.show({ color: "teal", message: t("batchDetail.markDefectOk") });
              } catch (e) {
                notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
              }
            }}>{t("batchDetail.markDefect")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

interface MoveTargetProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  label: string;
  options: { value: string; label: string }[];
  onConfirm: (value: string) => Promise<void>;
}

function MoveTargetModal({ opened, onClose, title, label, options, onConfirm }: MoveTargetProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (opened) setValue(null);
  }, [opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      <Stack>
        <Select
          label={label}
          data={options}
          value={value}
          onChange={setValue}
          searchable
          placeholder={t("common.select")}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!value}
            loading={submitting}
            onClick={async () => {
              if (!value) return;
              setSubmitting(true);
              try {
                await onConfirm(value);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {t("common.confirm")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface BulkEditProps {
  opened: boolean;
  onClose: () => void;
  count: number;
  onConfirm: (updates: Record<string, unknown>) => Promise<void>;
}

const ALL_STATUSES = [
  "received",
  "scanned",
  "keyed",
  "flashed",
  "tested",
  "labeled",
  "assigned",
  "shipped",
  "delivered",
  "returned",
  "defect",
];

function BulkEditModal({ opened, onClose, count, onConfirm }: BulkEditProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string | null>(null);
  const [boxNumber, setBoxNumber] = useState("");
  const [model, setModel] = useState("");
  const [firmwareVersion, setFirmwareVersion] = useState("");
  const [notesAppend, setNotesAppend] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (opened) {
      setStatus(null);
      setBoxNumber("");
      setModel("");
      setFirmwareVersion("");
      setNotesAppend("");
    }
  }, [opened]);

  async function submit() {
    const updates: Record<string, unknown> = {};
    if (status) updates.status = status;
    if (boxNumber) updates.box_number = boxNumber;
    if (model) updates.model = model;
    if (firmwareVersion) updates.firmware_version = firmwareVersion;
    if (notesAppend) updates.notes_append = notesAppend;
    if (Object.keys(updates).length === 0) {
      notifications.show({ color: "yellow", message: t("batchDetail.noFieldFilled") });
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(updates);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("batchDetail.bulkEditTitle", { count })} size="md">
      <Stack>
        <Text size="xs" c="dimmed">
          {t("batchDetail.bulkEditHint")}
        </Text>
        <Select
          label={t("batchDetail.statusLabel")}
          data={ALL_STATUSES.map((s) => ({ value: s, label: s }))}
          value={status}
          onChange={setStatus}
          clearable
          placeholder={t("batchDetail.statusKeep")}
        />
        <TextInput
          label={t("batchDetail.boxNumberLabel")}
          placeholder={t("batchDetail.keepEmptyHint")}
          value={boxNumber}
          onChange={(e) => setBoxNumber(e.currentTarget.value)}
        />
        <TextInput
          label={t("batchDetail.modelLabel")}
          value={model}
          onChange={(e) => setModel(e.currentTarget.value)}
        />
        <TextInput
          label={t("batchDetail.firmwareLabel")}
          value={firmwareVersion}
          onChange={(e) => setFirmwareVersion(e.currentTarget.value)}
        />
        <Textarea
          label={t("batchDetail.notesAppendLabel")}
          placeholder={t("batchDetail.notesAppendPlaceholder")}
          value={notesAppend}
          onChange={(e) => setNotesAppend(e.currentTarget.value)}
          autosize
          minRows={2}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} loading={submitting}>
            {t("common.apply")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface SingleEditProps {
  device: Device | null;
  onClose: () => void;
  onSaved: (d: Device) => void;
}

function SingleDeviceEditDrawer({ device, onClose, onSaved }: SingleEditProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>("");
  const [boxNumber, setBoxNumber] = useState("");
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [firmwareVersion, setFirmwareVersion] = useState("");
  const [appKey, setAppKey] = useState("");
  const [joinEui, setJoinEui] = useState("");
  const [qrPayload, setQrPayload] = useState("");
  const [notes, setNotes] = useState("");
  const [linkedEui, setLinkedEui] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!device) return;
    setStatus(device.status);
    setLinkedEui(device.linked_dev_eui ?? "");
    setBoxNumber(device.box_number ?? "");
    setModel(device.model ?? "");
    setSerialNumber(device.serial_number ?? "");
    setFirmwareVersion(device.firmware_version ?? "");
    setAppKey(device.app_key ?? "");
    setJoinEui(device.join_eui ?? "");
    setQrPayload((device as Device & { qr_payload?: string | null }).qr_payload ?? "");
    setNotes("");
  }, [device]);

  async function save() {
    if (!device) return;
    setSubmitting(true);
    try {
      const r = await api.updateDevice(device.id, {
        status: status as Device["status"],
        box_number: boxNumber || null,
        model: model || null,
        serial_number: serialNumber || null,
        firmware_version: firmwareVersion || null,
        app_key: appKey || null,
        join_eui: joinEui || null,
        qr_payload: qrPayload || null,
        notes: notes || null,
        ...(device.product_type_slug === "antenna" ? { linked_dev_eui: linkedEui.trim() || null } : {}),
      } as Partial<Device>);
      notifications.show({ color: "green", message: t("batchDetail.deviceUpdated") });
      onSaved(r.device);
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("common.saveFailed"),
        message: antennaErrorMessage(err, t) ?? JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      opened={device !== null}
      onClose={onClose}
      title={device ? `Device ${device.dev_eui.toLowerCase()}` : ""}
      position="right"
      size="md"
    >
      {device && (
        <Stack>
          <Select
            label={t("batchDetail.statusLabel")}
            data={ALL_STATUSES.map((s) => ({ value: s, label: s }))}
            value={status}
            onChange={(v) => setStatus(v ?? "")}
            allowDeselect={false}
          />
          <TextInput
            label={t("batchDetail.boxNumberLabel")}
            value={boxNumber}
            onChange={(e) => setBoxNumber(e.currentTarget.value)}
          />
          <TextInput
            label={t("batchDetail.modelLabel")}
            value={model}
            onChange={(e) => setModel(e.currentTarget.value)}
          />
          <TextInput
            label={t("batchDetail.serialLabel")}
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.currentTarget.value)}
          />
          <TextInput
            label={t("batchDetail.firmwareLabel")}
            value={firmwareVersion}
            onChange={(e) => setFirmwareVersion(e.currentTarget.value)}
          />
          <TextInput
            label={t("batchDetail.appKeyLabel")}
            value={appKey}
            onChange={(e) => setAppKey(e.currentTarget.value)}
          />
          <TextInput
            label={t("batchDetail.appEuiLabel")}
            value={joinEui}
            onChange={(e) => setJoinEui(e.currentTarget.value)}
          />
          <TextInput
            label={t("batchDetail.qrPayloadLabel")}
            value={qrPayload}
            onChange={(e) => setQrPayload(e.currentTarget.value)}
          />
          {device.product_type_slug === "antenna" && (
            <TextInput
              label={t("antennas.linkedModule")}
              description={t("antennas.linkedModuleHint")}
              value={linkedEui}
              onChange={(e) => setLinkedEui(e.currentTarget.value)}
              ff="monospace"
            />
          )}
          <Textarea
            label={t("batchDetail.notesOverwriteLabel")}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button onClick={save} loading={submitting}>
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

interface EditDrawerProps {
  opened: boolean;
  onClose: () => void;
  batch: Batch;
  onSaved: (b: Batch) => void;
}

function EditBatchDrawer({ opened, onClose, batch, onSaved }: EditDrawerProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState(batch.code);
  const [supplier, setSupplier] = useState(batch.supplier_name ?? "");
  const [model, setModel] = useState(batch.model ?? "");
  const [firmware, setFirmware] = useState(batch.firmware_version ?? "");
  const [received, setReceived] = useState(batch.received_date ?? "");
  const [notes, setNotes] = useState(batch.notes ?? "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setCode(batch.code);
    setSupplier(batch.supplier_name ?? "");
    setModel(batch.model ?? "");
    setFirmware(batch.firmware_version ?? "");
    setReceived(batch.received_date ?? "");
    setNotes(batch.notes ?? "");
  }, [batch]);

  async function save() {
    setSubmitting(true);
    try {
      const updated = await api.updateBatch(batch.id, {
        code,
        supplier_name: supplier || null,
        model: model || null,
        firmware_version: firmware || null,
        received_date: received || null,
        notes: notes || null,
      });
      notifications.show({ color: "green", message: t("batchDetail.batchUpdated") });
      onSaved(updated);
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("common.saveFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer opened={opened} onClose={onClose} title={t("batchDetail.editBatchTitle")} position="right" size="md">
      <Stack>
        <TextInput
          label={t("batchDetail.codeLabel")}
          required
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
        />
        <TextInput
          label={t("batchDetail.supplierLabel")}
          value={supplier}
          onChange={(e) => setSupplier(e.currentTarget.value)}
        />
        <TextInput
          label={t("batchDetail.modelLabel")}
          value={model}
          onChange={(e) => setModel(e.currentTarget.value)}
        />
        <TextInput
          label={t("batchDetail.firmwareLabel")}
          value={firmware}
          onChange={(e) => setFirmware(e.currentTarget.value)}
        />
        <TextInput
          label={t("batchDetail.receivedLabelEdit")}
          placeholder="2026-05-08"
          value={received}
          onChange={(e) => setReceived(e.currentTarget.value)}
        />
        <Textarea
          label={t("batchDetail.notesLabel")}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          autosize
          minRows={3}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={save} loading={submitting}>
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function ImportAntennasModal({ opened, onClose, batchId, onImported }: ImportProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [pasteText, setPasteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AntennaImportResult | null>(null);

  async function run(input: { text: string } | File) {
    setSubmitting(true);
    try {
      const r = await api.importBatchAntennas(batchId, input);
      setResult(r);
      onImported();
    } catch (err) {
      notifications.show({
        color: "red",
        title: t("batchDetail.importFailed"),
        message: antennaErrorMessage(err, t) ?? t("antennas.errors.generic"),
      });
    } finally {
      setSubmitting(false);
    }
  }

  const rows: { label: string; count: number; items?: string[]; color: string }[] = result
    ? [
        { label: t("antennas.created"), count: result.created, color: "green" },
        { label: t("antennas.invalid"), count: result.invalid.length, items: result.invalid, color: "red" },
        { label: t("antennas.exists"), count: result.exists.length, items: result.exists, color: "yellow" },
        { label: t("antennas.linked"), count: result.linked, color: "teal" },
        { label: t("antennas.linkNotFound"), count: result.link_not_found.length, items: result.link_not_found, color: "orange" },
      ]
    : [];

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setFile(null);
        setPasteText("");
        setResult(null);
        onClose();
      }}
      title={t("antennas.importTitle")}
      size="lg"
    >
      <Tabs defaultValue="paste">
        <Tabs.List>
          <Tabs.Tab value="paste" leftSection={<IconClipboardText size={14} />}>
            {t("antennas.tabPaste")}
          </Tabs.Tab>
          <Tabs.Tab value="file" leftSection={<IconUpload size={14} />}>
            {t("antennas.tabFile")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="paste" pt="md">
          <Stack>
            <Text size="sm" c="dimmed">
              {t("antennas.pasteHint")}
            </Text>
            <Textarea
              autosize
              minRows={6}
              maxRows={14}
              placeholder={"aabbccddeeff0011\naabbccddeeff0012;1122334455667788"}
              value={pasteText}
              onChange={(e) => setPasteText(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button onClick={() => run({ text: pasteText })} disabled={!pasteText.trim()} loading={submitting}>
                {t("antennas.import")}
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="file" pt="md">
          <Stack>
            <Text size="sm" c="dimmed">
              {t("antennas.fileHint")}
            </Text>
            <FileInput
              label={t("batchDetail.fileLabel")}
              placeholder={t("batchDetail.filePickerPlaceholder")}
              accept=".xlsx,.xlsm,.csv"
              value={file}
              onChange={setFile}
            />
            <Group justify="flex-end">
              <Button onClick={() => file && run(file)} disabled={!file} loading={submitting}>
                {t("antennas.import")}
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {result && (
        <Alert mt="md" color="blue" title={t("antennas.resultTitle")}>
          <Stack gap={4}>
            {rows.map((r) => (
              <div key={r.label}>
                <Group gap="xs">
                  <Badge color={r.color} variant="light">{r.count}</Badge>
                  <Text size="sm">{r.label}</Text>
                </Group>
                {r.items && r.items.length > 0 && (
                  <Text size="xs" c="dimmed" ff="monospace" style={{ wordBreak: "break-all" }}>
                    {r.items.slice(0, 20).join(", ")}
                    {r.items.length > 20 ? ` … (+${r.items.length - 20})` : ""}
                  </Text>
                )}
              </div>
            ))}
          </Stack>
        </Alert>
      )}
    </Modal>
  );
}

interface ImportProps {
  opened: boolean;
  onClose: () => void;
  batchId: number;
  onImported: () => void;
}

function ImportDevicesModal({ opened, onClose, batchId, onImported }: ImportProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [pasteText, setPasteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [mapping, setMapping] = useState<ImportMapping | null>(null);
  const [headerRow, setHeaderRow] = useState(0);
  const [sheet, setSheet] = useState("");
  const [joinEuiDefault, setJoinEuiDefault] = useState("");
  const [appKeyDefault, setAppKeyDefault] = useState("");

  function reset() {
    setFile(null);
    setPasteText("");
    setResult(null);
    setPreview(null);
    setMapping(null);
  }

  /** Reads the file's columns and proposes a mapping: the one saved for this
   * header layout if any, else the server's guess. */
  async function loadPreview(f: File, opts: { sheet?: string; header_row?: number } = {}) {
    setPreviewing(true);
    setResult(null);
    try {
      const p = await api.previewBatchImport(batchId, f, opts);
      setPreview(p);
      setSheet(p.sheet);
      setHeaderRow(p.header_row);
      const saved = loadSavedMapping(p);
      setMapping(saved?.mapping ?? p.mapping);
      setJoinEuiDefault(saved?.join_eui_default || p.suggested_join_eui || "");
    } catch (err) {
      setPreview(null);
      setMapping(null);
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("batchImport.previewFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setPreviewing(false);
    }
  }

  function onFileChange(f: File | null) {
    setFile(f);
    setPreview(null);
    setMapping(null);
    setAppKeyDefault("");
    if (f) void loadPreview(f);
  }

  function notifyImportResult(r: ImportResult) {
    const color = r.inserted > 0 ? "green" : r.skipped > 0 ? "yellow" : "red";
    const lines: string[] = [
      t("batchDetail.importToast", { inserted: r.inserted, skipped: r.skipped, total: r.total_rows }),
    ];
    if (r.skipped_by_batch && Object.keys(r.skipped_by_batch).length > 0) {
      const breakdown = Object.entries(r.skipped_by_batch)
        .map(([code, n]) => `${code}: ${n}`)
        .join(" · ");
      lines.push(t("batchDetail.importAlreadyIn", { breakdown }));
    }
    notifications.show({
      color,
      title: r.inserted > 0 ? t("batchDetail.importOk") : t("batchDetail.importNoNew"),
      message: lines.join("\n"),
      autoClose: r.inserted > 0 ? 4000 : 7000,
      style: { whiteSpace: "pre-line" },
    });
  }

  async function handleXlsxImport() {
    if (!file) return;
    setSubmitting(true);
    try {
      let r: ImportResult;
      if (preview && mapping) {
        r = await api.importBatchXlsx(batchId, file, {
          mapping,
          header_row: headerRow,
          sheet,
          join_eui_default: joinEuiDefault.trim() || undefined,
          app_key_default: appKeyDefault.trim() || undefined,
        });
        saveMapping(preview, { mapping, join_eui_default: joinEuiDefault.trim() });
      } else {
        r = await api.importBatchXlsx(batchId, file);
      }
      setResult(r);
      notifyImportResult(r);
      onImported();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("batchDetail.importFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePasteImport() {
    if (!pasteText.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.importBatchPaste(batchId, pasteText);
      setResult(r);
      notifyImportResult(r);
      onImported();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("batchDetail.importFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t("batchDetail.importTitle")}
      size="xl"
    >
      <Tabs defaultValue="xlsx">
        <Tabs.List>
          <Tabs.Tab value="xlsx" leftSection={<IconUpload size={14} />}>
            {t("batchDetail.tabUploadXlsx")}
          </Tabs.Tab>
          <Tabs.Tab value="paste" leftSection={<IconClipboardText size={14} />}>
            {t("batchDetail.tabPaste")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="xlsx" pt="md">
          <Stack>
            <Text size="sm" c="dimmed">
              {t("batchDetail.xlsxHint")}
            </Text>
            <FileInput
              label={t("batchDetail.fileLabel")}
              placeholder={t("batchDetail.filePickerPlaceholder")}
              accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              value={file}
              onChange={onFileChange}
            />
            {previewing && <Loader size="sm" />}
            {file && preview && mapping && (
              <ImportColumnMapper
                preview={preview}
                mapping={mapping}
                onMappingChange={setMapping}
                headerRow={headerRow}
                onHeaderRowChange={(v) => void loadPreview(file, { sheet, header_row: v })}
                sheet={sheet}
                onSheetChange={(v) => void loadPreview(file, { sheet: v })}
                joinEuiDefault={joinEuiDefault}
                onJoinEuiDefaultChange={setJoinEuiDefault}
                appKeyDefault={appKeyDefault}
                onAppKeyDefaultChange={setAppKeyDefault}
              />
            )}
            <Group justify="flex-end">
              <Button
                onClick={handleXlsxImport}
                disabled={
                  !file ||
                  previewing ||
                  (!!mapping && !mappingValid(mapping, joinEuiDefault, appKeyDefault))
                }
                loading={submitting}
              >
                {t("batchDetail.import")}
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="paste" pt="md">
          <Stack>
            <Text size="sm" c="dimmed">
              {t("batchDetail.pasteHint")}
            </Text>
            <Textarea
              autosize
              minRows={6}
              maxRows={14}
              placeholder={"aabbccddeeff0011\naabbccddeeff0012\n..."}
              value={pasteText}
              onChange={(e) => setPasteText(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button onClick={handlePasteImport} disabled={!pasteText.trim()} loading={submitting}>
                {t("batchDetail.import")}
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {result && (
        <Alert
          mt="md"
          color={
            result.inserted > 0
              ? result.issues.length > 0
                ? "yellow"
                : "green"
              : result.skipped > 0
                ? "yellow"
                : "red"
          }
          title={
            result.inserted > 0
              ? t("batchDetail.importResult")
              : t("batchDetail.importNoNewLong")
          }
        >
          <Stack gap={4}>
            <Text
              size="sm"
              dangerouslySetInnerHTML={{
                __html: t("batchDetail.importStats", {
                  inserted: result.inserted,
                  skipped: result.skipped,
                  total: result.total_rows,
                }),
              }}
            />
            {result.backfilled_serial != null && result.backfilled_serial > 0 && (
              <Text
                size="xs"
                dangerouslySetInnerHTML={{
                  __html: t("batchDetail.backfilled", { count: result.backfilled_serial }),
                }}
              />
            )}
            {result.skipped_by_batch && Object.keys(result.skipped_by_batch).length > 0 && (
              <Stack gap={2} mt={4}>
                <Text size="xs" c="dimmed">
                  {t("batchDetail.alreadyInBatches")}
                </Text>
                {Object.entries(result.skipped_by_batch).map(([batchCode, count]) => (
                  <Text key={batchCode} size="xs">
                    • <b>{batchCode}</b>: {count}
                  </Text>
                ))}
              </Stack>
            )}
            {result.issues.length > 0 && (
              <Text size="xs" c="dimmed" mt={4}>
                Issues: {result.issues.slice(0, 5).join("; ")}
                {result.issues.length > 5 && ` (+${result.issues.length - 5})`}
              </Text>
            )}
          </Stack>
        </Alert>
      )}
    </Modal>
  );
}
